(() => {
  'use strict';

  const GIFTS = {
    tester3: 53311499960584,
    tester5: 53311579095304,
    shopper: 52795445903624,
    sockYarn: 58334976901384
  };

  const TAGS = {
    shopper: 'gift-shopper',
    sockYarn: 'gift-sock-yarn'
  };

  const GIFT_IDS = new Set(
    Object.values(GIFTS).map(id => String(id))
  );

  let syncing = false;
  let pendingSync = false;
  let syncTimer = null;
  let reloading = false;


  /* =========================================================
     ROUTES
     ========================================================= */

  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function route(path) {
    return shopRoot().replace(/\/?$/, '/') +
      String(path).replace(/^\//, '');
  }


  /* =========================================================
     HELPERS
     ========================================================= */

  function variantId(item) {
    return String(item?.variant_id || item?.id || '');
  }

  function isGift(item) {
    return GIFT_IDS.has(variantId(item));
  }

  function isCartPage() {
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '/cart' || path.endsWith('/cart');
  }


  /* =========================================================
     TAGS
     ========================================================= */

  function normalizeTag(tag) {
    return String(tag || '')
      .trim()
      .toLowerCase()
      .replace(/[‐-‒–—―−]/g, '-')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function productTags(product) {
    if (!product) return [];

    let tags = product.tags || [];

    if (!Array.isArray(tags)) {
      tags = String(tags).split(',');
    }

    return tags
      .map(normalizeTag)
      .filter(Boolean);
  }

  function hasTag(product, requiredTag) {
    return productTags(product)
      .includes(normalizeTag(requiredTag));
  }


  /* =========================================================
     PRODUCT HANDLE
     ========================================================= */

  function getHandle(item) {
    if (!item?.url) return null;

    try {
      const url = new URL(
        item.url,
        window.location.origin
      );

      const match =
        url.pathname.match(/\/products\/([^/]+)/);

      return match
        ? decodeURIComponent(match[1])
        : null;

    } catch {
      const match =
        String(item.url).match(/\/products\/([^/?]+)/);

      return match
        ? decodeURIComponent(match[1])
        : null;
    }
  }


  /* =========================================================
     CART API
     ========================================================= */

  async function getCart() {
    const response = await fetch(
      route('cart.js'),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart read failed: ${response.status}`
      );
    }

    return response.json();
  }


  async function getProduct(handle) {
    if (!handle) return null;

    try {
      const response = await fetch(
        route(`products/${encodeURIComponent(handle)}.js`),
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        return null;
      }

      return response.json();

    } catch {
      return null;
    }
  }


  async function updateCart(updates) {
    if (!updates || !Object.keys(updates).length) {
      return null;
    }

    const response = await fetch(
      route('cart/update.js'),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          updates
        })
      }
    );

    if (!response.ok) {
      const text = await response.text();

      throw new Error(
        `Cart update failed: ${response.status} ${text}`
      );
    }

    return response.json();
  }


  async function addGift(id, name) {
    const response = await fetch(
      route('cart/add.js'),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(id),
              quantity: 1,
              properties: {
                _strick_addicts_gift: 'true'
              }
            }
          ]
        })
      }
    );

    if (!response.ok) {
      const message = await response.text();

      console.error(
        `[SA Gifts] Could not add ${name}:`,
        response.status,
        message
      );

      return false;
    }

    return true;
  }


  /* =========================================================
     RELOAD CART PAGE
     ========================================================= */

  function reloadCartPage() {
    if (!isCartPage() || reloading) {
      return;
    }

    reloading = true;

    /*
      Shopify has already finished all gift changes
      before we reach this point.

      Small delay only lets the browser finish the
      current event cycle before reloading.
    */
    setTimeout(() => {
      window.location.reload();
    }, 150);
  }


  /* =========================================================
     MAIN GIFT LOGIC
     ========================================================= */

  async function syncGifts() {

    if (syncing) {
      pendingSync = true;
      return;
    }

    syncing = true;
    pendingSync = false;

    let changed = false;

    try {

      const cart = await getCart();

      const normalItems =
        cart.items.filter(item => !isGift(item));

      const giftItems =
        cart.items.filter(item => isGift(item));


      /* -----------------------------------------------------
         EMPTY CART
         ----------------------------------------------------- */

      if (!normalItems.length) {

        const updates = {};

        for (const item of giftItems) {
          updates[item.key] = 0;
        }

        if (Object.keys(updates).length) {
          await updateCart(updates);
          changed = true;
        }

        if (changed) {
          reloadCartPage();
        }

        return;
      }


      /* -----------------------------------------------------
         MERCHANDISE TOTAL
         ----------------------------------------------------- */

      const merchandiseTotal =
        normalItems.reduce(
          (sum, item) =>
            sum +
            Number(
              item.final_line_price ??
              item.line_price ??
              0
            ),
          0
        );


      /* -----------------------------------------------------
         TESTER
         ----------------------------------------------------- */

      const desiredTester =
        merchandiseTotal < 5000
          ? GIFTS.tester3
          : GIFTS.tester5;


      /* -----------------------------------------------------
         LOAD PRODUCTS
         ----------------------------------------------------- */

      const handles = [
        ...new Set(
          normalItems
            .map(getHandle)
            .filter(Boolean)
        )
      ];

      const products = await Promise.all(
        handles.map(async handle => ({
          handle,
          product: await getProduct(handle)
        }))
      );


      /* -----------------------------------------------------
         TAG RULES
         ----------------------------------------------------- */

      let wantsShopper = false;
      let wantsSockYarn = false;

      for (const entry of products) {

        const product = entry.product;

        if (!product) continue;

        if (
          hasTag(
            product,
            TAGS.shopper
          )
        ) {
          wantsShopper = true;
        }

        if (
          hasTag(
            product,
            TAGS.sockYarn
          )
        ) {
          wantsSockYarn = true;
        }
      }


      /* -----------------------------------------------------
         DESIRED GIFTS
         ----------------------------------------------------- */

      const desired = new Map();

      desired.set(
        String(desiredTester),
        {
          id: desiredTester,
          name:
            merchandiseTotal < 5000
              ? 'Tester 3'
              : 'Tester 5'
        }
      );

      if (wantsShopper) {
        desired.set(
          String(GIFTS.shopper),
          {
            id: GIFTS.shopper,
            name: 'Shopper'
          }
        );
      }

      if (wantsSockYarn) {
        desired.set(
          String(GIFTS.sockYarn),
          {
            id: GIFTS.sockYarn,
            name: 'ROWAN SOCK'
          }
        );
      }


      /* -----------------------------------------------------
         CHECK EXISTING GIFTS
         ----------------------------------------------------- */

      const existing = new Set();
      const updates = {};

      for (const item of giftItems) {

        const id = variantId(item);

        /*
          Gift is no longer required
        */
        if (!desired.has(id)) {
          updates[item.key] = 0;
          continue;
        }

        /*
          Duplicate gift
        */
        if (existing.has(id)) {
          updates[item.key] = 0;
          continue;
        }

        existing.add(id);

        /*
          Gift quantity must always be 1
        */
        if (Number(item.quantity) !== 1) {
          updates[item.key] = 1;
        }
      }


      /* -----------------------------------------------------
         REMOVE / CORRECT GIFTS
         ----------------------------------------------------- */

      if (Object.keys(updates).length) {
        await updateCart(updates);
        changed = true;
      }


      /* -----------------------------------------------------
         ADD MISSING GIFTS
         ----------------------------------------------------- */

      for (const [id, gift] of desired) {

        if (existing.has(id)) {
          continue;
        }

        const success = await addGift(
          gift.id,
          gift.name
        );

        if (success) {
          changed = true;
        }
      }


      /* -----------------------------------------------------
         CART PAGE

         Reload ONLY after Shopify has completed
         all gift changes.
         ----------------------------------------------------- */

      if (changed) {
        reloadCartPage();
      }


    } catch (error) {

      console.error(
        '[Strick Addicts Gifts]',
        error
      );

    } finally {

      syncing = false;

      if (pendingSync && !reloading) {
        pendingSync = false;
        scheduleSync(450);
      }
    }
  }


  /* =========================================================
     EVENT SCHEDULING

     This is NOT polling.
     It runs only after an event.
     ========================================================= */

  function scheduleSync(delay = 350) {

    if (reloading) {
      return;
    }

    clearTimeout(syncTimer);

    syncTimer = setTimeout(
      syncGifts,
      delay
    );
  }


  /* =========================================================
     SLEEK / SHOPIFY EVENTS
     ========================================================= */

  /*
    Product added to cart
  */
  document.addEventListener(
    'product-ajax:added',
    () => {
      scheduleSync(400);
    }
  );


  /*
    Product removed or quantity changed
  */
  document.addEventListener(
    'cart:updated',
    () => {
      if (!syncing) {
        scheduleSync(400);
      }
    }
  );


  /* =========================================================
     INITIAL CHECK

     One check when page loads.
     This also repairs a stale cart if necessary.
     ========================================================= */

  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleSync(600);
      },
      {
        once: true
      }
    );

  } else {

    scheduleSync(600);
  }

})();